import React, { useMemo, useState, useEffect } from 'react';
import {
  User,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useSelector } from 'react-redux';

import EmployeeSearch from '../../components/common/EmployeeSearch';
import leaveService from '../../services/LeaveService';

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const fmt = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return '';
  }
  return String(value).trim();
};

// ─────────────────────────────────────────────────────────────
// PAGINATION COMPONENT
// ─────────────────────────────────────────────────────────────

function Pagination({ currentPage, totalPages, onPageChange, itemsCount, totalItems }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
      <div className="text-sm text-gray-500">
        Showing <span className="font-medium">{itemsCount}</span> of{' '}
        <span className="font-medium">{totalItems}</span> entries
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={16} />
        </button>

        {getPageNumbers().map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:bg-gray-50'
              }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

export default function EmployeeLeaveAllocation() {
  // ─────────────────────────────────────────────────────────
  // EMPLOYEES FROM REDUX
  // ─────────────────────────────────────────────────────────

  const employeesFromRedux = useSelector(
    (state) =>
      state.employees?.employees ||
      state.employees ||
      []
  );

  // ─────────────────────────────────────────────────────────
  // NORMALIZE EMPLOYEES FOR SHARED EmployeeSearch
  // ─────────────────────────────────────────────────────────

  const employeeOptions = useMemo(() => {
    if (!Array.isArray(employeesFromRedux)) return [];

    return employeesFromRedux
      .map((employee) => {
        const serviceNo =
          employee.p_ced_service_no ||
          employee.HED_SERVICE_NO ||
          employee.HED_CED_SERVICE_NO ||
          employee.serviceNo ||
          '';

        const firstName =
          employee.p_first_name ||
          employee.HED_FIRST_NAME ||
          employee.firstName ||
          '';

        const lastName =
          employee.p_last_name ||
          employee.HED_LAST_NAME ||
          employee.lastName ||
          '';

        const name =
          employee.p_report_name ||
          employee.HED_REPORT_NAME ||
          employee.name ||
          `${firstName} ${lastName}`.trim() ||
          'Unnamed Employee';

        return {
          employeeId: String(serviceNo || employee.HED_EMPLOYEE_ID || employee.employeeId || ''),
          serviceNo: String(serviceNo),
          name,
          department:
            employee.p_department_name ||
            employee.HDD_DEPARTMENT_NAME ||
            '',
        };
      })
      .filter((employee) => fmt(employee.employeeId) !== '');
  }, [employeesFromRedux]);

  // ─────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────

  const [selectedServiceNo, setSelectedServiceNo] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 7;

  // ─────────────────────────────────────────────────────────
  // FETCH LEAVE ALLOCATION
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedServiceNo) {
      setAllocations([]);
      setError('');
      setCurrentPage(1);
      return;
    }

    const fetchLeaveAllocation = async () => {
      try {
        setLoading(true);
        setError('');
        setAllocations([]);

        const response = await leaveService.GetEmpLeaveAllocation(
          selectedServiceNo
        );

        const data = Array.isArray(response?.ResultSet)
          ? response.ResultSet
          : [];

        setAllocations(data);
        setCurrentPage(1);
      } catch (err) {
        console.error('[LeaveAllocation] Error fetching allocation:', err);
        setAllocations([]);
        setError(err?.message || 'Failed to load leave allocation.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveAllocation();
  }, [selectedServiceNo]);

  // ─────────────────────────────────────────────────────────
  // EMPLOYEE SELECTION
  // ─────────────────────────────────────────────────────────

  const handleEmployeeSelect = (employee) => {
    if (!employee) {
      // Shared component sends null when cleared
      setSelectedServiceNo('');
      setSelectedEmployee(null);
      return;
    }

    setSelectedServiceNo(employee.serviceNo);
    setSelectedEmployee(employee);
  };

  // ─────────────────────────────────────────────────────────
  // MAP API DATA TO TABLE DATA
  // ─────────────────────────────────────────────────────────

  const rows = useMemo(() => {
    return allocations.map((allocation, index) => ({
      id: allocation.HLA_ALLOCATION_ID || index,
      leaveType: allocation.HLT_NAME || '—',
      allocated: Number(allocation.HLA_ALLOCATED_DAYS || 0),
      adjustment: Number(allocation.HLA_ADJUSTMENT_DAYS || 0),
      earned: Number(allocation.HLA_EARNED_DAYS || 0),
      used: Number(allocation.HLA_USED_DAYS || 0),
      balance: Number(allocation.HLA_BALANCE_DAYS || 0),
      leaveCode: allocation.HLT_CODE || '',
      leaveTypeId: allocation.HLA_LEAVE_TYPE_ID || '',
      leaveYear: allocation.HLA_LEAVE_YEAR || '',
      generatedDate: allocation.HLA_GENERATED_DATE || '',
    }));
  }, [allocations]);

  // ─────────────────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────────────────

  const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = Math.min(startIndex + ROWS_PER_PAGE, rows.length);
  const currentRows = rows.slice(startIndex, endIndex);

  // ─────────────────────────────────────────────────────────
  // EMPLOYEE NAME
  // ─────────────────────────────────────────────────────────

  const employeeName = selectedEmployee
    ? selectedEmployee.name || ''
    : '';

  // ─────────────────────────────────────────────────────────
  // TOTALS
  // ─────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    return rows.reduce(
      (total, row) => ({
        allocated: total.allocated + row.allocated,
        adjustment: total.adjustment + row.adjustment,
        earned: total.earned + row.earned,
        used: total.used + row.used,
        balance: total.balance + row.balance,
      }),
      {
        allocated: 0,
        adjustment: 0,
        earned: 0,
        used: 0,
        balance: 0,
      }
    );
  }, [rows]);

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col p-4 space-y-3">
      <div className="flex-1 min-h-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col">

        {/* ─────────────────────────────────────────────── */}
        {/* EMPLOYEE SELECTOR */}
        {/* ─────────────────────────────────────────────── */}

        <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Select Employee:
          </span>

          <div className="flex-1 max-w-md">
            <EmployeeSearch
              value={selectedEmployee}
              onSelect={handleEmployeeSelect}
              employees={employeeOptions}
              loading={loading && !selectedServiceNo}
              showAllOption={false}
            />
          </div>

          {selectedEmployee && (
            <div className="ml-auto text-sm text-gray-500">
              <span className="font-medium text-gray-700">Selected:</span>{' '}
              {employeeName}
              <span className="ml-2 text-xs text-gray-400">
                ({selectedServiceNo})
              </span>
            </div>
          )}
        </div>

        {/* ─────────────────────────────────────────────── */}
        {/* TABLE */}
        {/* ─────────────────────────────────────────────── */}

        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-[740px] border-collapse text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50">
                {[
                  'Leave Type',
                  'Allocated',
                  'Adjustments',
                  'Earned',
                  'Used',
                  'Balance',
                ].map((heading) => (
                  <th
                    key={heading}
                    className="whitespace-nowrap px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* NO EMPLOYEE */}
              {!selectedServiceNo && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <User size={24} className="text-gray-300" />
                      <span>
                        Please select an employee to view leave allocation
                      </span>
                    </div>
                  </td>
                </tr>
              )}

              {/* LOADING */}
              {selectedServiceNo && loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      <Loader2
                        size={18}
                        className="animate-spin text-primary-500"
                      />
                      Loading leave allocation...
                    </div>
                  </td>
                </tr>
              )}

              {/* ERROR */}
              {selectedServiceNo && !loading && error && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-10 text-center text-sm text-red-500"
                  >
                    {error}
                  </td>
                </tr>
              )}

              {/* NO DATA */}
              {selectedServiceNo &&
                !loading &&
                !error &&
                rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-10 text-center text-sm text-gray-400"
                    >
                      No leave allocation configured for{' '}
                      {employeeName || 'this employee'} yet.
                    </td>
                  </tr>
                )}

              {/* API DATA */}
              {!loading &&
                !error &&
                currentRows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={index % 2 === 1 ? 'bg-surface-50/40' : ''}
                  >
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {row.leaveType}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {row.allocated.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {row.adjustment.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {row.earned.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {row.used.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 font-semibold text-primary-700">
                      {row.balance.toFixed(2)}
                    </td>
                  </tr>
                ))}

              {/* TOTALS ROW */}
              {selectedServiceNo &&
                !loading &&
                !error &&
                rows.length > 0 && (
                  <tr className="border-t-2 border-gray-300">
                    <td className="px-5 py-3 font-bold text-gray-900">
                      <span className="text-sm font-bold">Totals</span>
                    </td>
                    <td className="px-5 py-3 font-bold text-gray-800">
                      {totals.allocated.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 font-bold text-gray-600">
                      {totals.adjustment.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 font-bold text-gray-800">
                      {totals.earned.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 font-bold text-gray-800">
                      {totals.used.toFixed(2)}
                    </td>
                    <td className="px-5 py-3 font-bold text-primary-600 text-lg">
                      {totals.balance.toFixed(2)}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>

        {/* ─────────────────────────────────────────────── */}
        {/* PAGINATION */}
        {/* ─────────────────────────────────────────────── */}

        {selectedServiceNo &&
          !loading &&
          !error &&
          rows.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsCount={currentRows.length}
              totalItems={rows.length}
            />
          )}
      </div>
    </div>
  );
}